import { useMemo } from "react";
import {
  DataTable,
  DateColumnFilter,
  SelectColumnFilter,
  TableDebug,
} from "../DataTable";
import { OverlayTrigger } from "react-bootstrap";
import { formatDate } from "../../helpers/date.util";

import { pieceTypes } from "../../constants";

import { useGetHistoryReturnRequest } from "./returnRequestHandler.util";
import { TooltipStandard } from "../Utility/TooltipStandard";

const pieceTypeOptions = Object.values(pieceTypes).map((type) => ({
  label: type,
  value: type,
}));

const getPieceTypeLabel = (value) => {
  const pieceType = pieceTypeOptions.find((type) => type.value === value);

  return pieceType?.label || "Pieces";
};

function CustomerCell({ row }) {
  const { original } = row;

  return (
    <div className="tw-flex tw-items-start tw-flex-col">
      <div className="tw-whitespace-normal">{original.customerName}</div>
      <div className="tw-whitespace-normal">{original.customerAddress}</div>
    </div>
  );
}

const renderWithToolbar = (showHistory, onShowHistory) => () =>
  (
    <OverlayTrigger
      flip
      placement="top"
      overlay={(props) => (
        <TooltipStandard {...props}>
          Show pending approved/rejected return orders
        </TooltipStandard>
      )}
    >
      <div className="tw-inline-flex tw-items-center">
        <div className="custom-control custom-switch custom-switch-md tw-pb-2">
          <input
            id="show-history-switch"
            type="checkbox"
            checked={showHistory}
            onChange={onShowHistory}
            className="custom-control-input cursor-pointer"
          />

          <label
            className="custom-control-label cursor-pointer p-0"
            htmlFor="show-history-switch"
          />
        </div>
        <span className="tw-text-lg tw-font-medium tw-text-gray-900 tw-ml-4">
          View History
        </span>
      </div>
    </OverlayTrigger>
  );

function HistoryReturnRequestTable({ showHistory, onShowHistory }) {
  const { data: response, isLoading, isError } = useGetHistoryReturnRequest();
  const column = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
      },
      {
        Header: "Delivery ID",
        accessor: "deliveryId",
        maxWidth: 250,
        minWidth: 180,
        width: 180,
      },
      {
        Header: "Invoice Number",
        accessor: "invoiceNumber",
        maxWidth: 250,
        minWidth: 180,
        width: 180,
      },
      {
        Header: "Customer ID",
        accessor: "customerId",
        maxWidth: 250,
        minWidth: 180,
        width: 180,
      },
      {
        Header: "Customer",
        id: "customer",
        accessor: (originalRow) =>
          `${originalRow.customerName} ${originalRow.customerAddress}`,
        Cell: CustomerCell,
      },
      {
        Header: "Return Quantity",
        accessor: (originalRow) =>
          `${originalRow.quantity} ${getPieceTypeLabel(originalRow.pieceType)}`,
      },
      {
        Header: "Original Pieces",
        accessor: "totalPieces",
      },
      {
        Header: "Return Date",
        accessor: "returnDate",
        Cell: ({ value }) => formatDate(value, "MM/DD/YYYY hh:mm A"),
        Filter: DateColumnFilter,
        filter: "dateEqual",
        minWidth: 180,
        width: 200,
        maxWidth: 250,
      },
      {
        Header: "Delivery Date",
        accessor: "deliveryDate",
        Cell: ({ value }) => formatDate(value, "MM/DD/YYYY hh:mm A"),
        Filter: DateColumnFilter,
        filter: "dateEqual",
        minWidth: 180,
        width: 200,
        maxWidth: 250,
      },
      {
        Header: "Comment",
        accessor: "comment",
        minWidth: 200,
        width: 200,
        maxWidth: 300,
      },
      {
        Header: "Request Return",
        id: "requestReturn", // required because our accessor is not a string
        accessor: (originalRow) => {
          if (originalRow?.isApprove || originalRow?.isImported)
            return "Approved";

          if (originalRow?.isDenied) return "Denied";

          return "Pending";
        },
        Filter: SelectColumnFilter,
        width: 180,
        disableResizing: true,
      },
    ],
    []
  );

  return (
    <div>
      <div className="paper-panel-tight tw-rounded-2xl tw-bg-white tw-pb-5 tw-pt-4 tw-px-3">
        <DataTable
          columns={column}
          data={Array.isArray(response?.data) ? response?.data : []}
          isLoading={isLoading}
          isError={isError}
          viewDebugMode
          initialState={{
            hiddenColumns: ["id"],
          }}
          renderWithTopToolbar={renderWithToolbar(showHistory, onShowHistory)}
        />
      </div>

      <TableDebug
        className="tw-mt-5"
        data={Array.isArray(response?.data) ? response?.data : []}
        viewDebugMode
      />
    </div>
  );
}

export default HistoryReturnRequestTable;
